/** The git panel's last graph per repo, kept in this browser so opening the
 *  panel draws at once while the bridge fetches and reads it again. Only ever a
 *  first paint: the next read replaces it. Per browser, a few repos at most,
 *  and dropped on sign-out — the graph is per reader (its tasks, Claude's first
 *  prompts), so a shared browser must not show it to the next person. The
 *  "Other tasks" tree is kept with it, so it draws before the board has loaded. */
import type { GitGraph } from "@shared/gitGraph.js";
import type { Task } from "@shared/types.js";

const PREFIX = "todos.gitGraph:";
/** The panel's "Other tasks" tree for a repo, kept beside its graph and dropped with it. */
const OTHER_PREFIX = "todos.gitOther:";
/** Repos, most recently saved first. */
const INDEX_KEY = "todos.gitGraphs";
const KEEP = 6;

function index(): string[] {
  try {
    const list: unknown = JSON.parse(localStorage.getItem(INDEX_KEY) ?? "[]");
    return Array.isArray(list) ? list.filter((r): r is string => typeof r === "string") : [];
  } catch {
    return [];
  }
}

export function readCachedGraph(repo: string): GitGraph | null {
  try {
    const raw = localStorage.getItem(PREFIX + repo);
    if (!raw) return null;
    const g = JSON.parse(raw) as GitGraph;
    return g && g.repo === repo && Array.isArray(g.commits) ? g : null;
  } catch {
    return null;
  }
}

export function saveCachedGraph(graph: GitGraph): void {
  let repos = [graph.repo, ...index().filter((r) => r !== graph.repo)];
  const raw = JSON.stringify(graph);
  // Over quota: drop the oldest other repo and try again, until only this one is left.
  for (;;) {
    for (const r of repos.slice(KEEP)) remove(r);
    repos = repos.slice(0, KEEP);
    try {
      localStorage.setItem(PREFIX + graph.repo, raw);
      localStorage.setItem(INDEX_KEY, JSON.stringify(repos));
      return;
    } catch {
      if (repos.length <= 1) {
        remove(graph.repo);
        return;
      }
      remove(repos.pop()!);
    }
  }
}

export function clearCachedGraphs(): void {
  for (const r of index()) remove(r);
  try {
    localStorage.removeItem(INDEX_KEY);
  } catch {
    // Nothing was kept.
  }
}

/** A task of the "Other tasks" tree as kept: only what a row draws, and the
 *  board a root comes from when it is not the one on screen. */
export type KeptTask = Pick<Task, "id" | "parentId" | "teamId" | "privateTo" | "rank" | "title" | "status" | "urgency" | "repo"> & {
  board: string | null;
};

export function readCachedOther(repo: string): KeptTask[] | null {
  try {
    const raw = localStorage.getItem(OTHER_PREFIX + repo);
    if (!raw) return null;
    const list: unknown = JSON.parse(raw);
    return Array.isArray(list) ? (list as KeptTask[]) : null;
  } catch {
    return null;
  }
}

/** Kept only for a repo whose graph is kept, so it goes when that does. */
export function saveCachedOther(repo: string, tasks: KeptTask[]): void {
  if (!index().includes(repo)) return;
  try {
    const raw = JSON.stringify(tasks);
    if (localStorage.getItem(OTHER_PREFIX + repo) !== raw) localStorage.setItem(OTHER_PREFIX + repo, raw);
  } catch {
    // Storage blocked or full: the next visit waits for the board.
  }
}

function remove(repo: string): void {
  try {
    localStorage.removeItem(PREFIX + repo);
    localStorage.removeItem(OTHER_PREFIX + repo);
  } catch {
    // Storage blocked: nothing was kept.
  }
}
